import type { ServiceRun, ServiceRunStatus } from '../api/monitor';
import { pageRange } from './pagination';

interface RunTone {
  label: string;
  /** Classe da pílula (fundo + texto). */
  pill: string;
}

/**
 * `running` fica em amarelo junto com "parcial": tanto faz se o job ainda está
 * trabalhando ou se o processo morreu no meio e deixou a linha presa — nos dois
 * casos ninguém pode afirmar que aquela execução terminou bem.
 */
export const RUN_TONE: Record<ServiceRunStatus, RunTone> = {
  ok: { label: 'Concluído', pill: 'bg-ok-100 text-ok-600' },
  error: { label: 'Falhou', pill: 'bg-danger-100 text-danger-600' },
  running: { label: 'Em execução', pill: 'bg-warn-100 text-warn-700' },
};

export function formatDuration(durationMs: number | null): string {
  if (durationMs === null) return '—';
  if (durationMs < 1000) return `${durationMs} ms`;
  if (durationMs < 60_000) return `${(durationMs / 1000).toFixed(1)} s`;

  return `${Math.floor(durationMs / 60_000)} min ${Math.round((durationMs % 60_000) / 1000)} s`;
}

/** "4 itens · 1 falha · 820 ms" — o resumo da rodada em uma linha. */
export function runDetail(run: ServiceRun): string {
  const parts = [`${run.processed} ${run.processed === 1 ? 'item' : 'itens'}`];

  if (run.failed > 0) parts.push(`${run.failed} ${run.failed === 1 ? 'falha' : 'falhas'}`);
  if (run.status !== 'running') parts.push(formatDuration(run.durationMs));
  if (run.triggeredBy === 'manual') parts.push('disparo manual');
  if (run.triggeredBy === 'queue') parts.push('publicação recebida');

  return parts.join(' · ');
}

const WEEKDAYS = [
  'domingo',
  'segunda-feira',
  'terça-feira',
  'quarta-feira',
  'quinta-feira',
  'sexta-feira',
  'sábado',
];

/**
 * Expressão cron em português.
 *
 * Cobre só os formatos que os jobs do SICOP usam hoje ("a cada N minutos",
 * "todo dia às HH:MM" e a variação por dia da semana); qualquer outra coisa
 * volta como a própria expressão, que é uma resposta pior mas nunca errada —
 * traduzir cron inteiro exigiria uma biblioteca, e a tela mostra a próxima
 * execução calculada pelo servidor logo ao lado.
 *
 * Nulo é o job que não tem agendamento nenhum: quem decide quando ele roda é
 * outro sistema, publicando na fila.
 */
export function cronLabel(expression: string | null): string {
  if (!expression) return 'quando o SGC publicar';

  const parts = expression.trim().split(/\s+/);

  if (parts.length !== 5) return expression;

  const [minute, hour, day, month, weekday] = parts;
  const everyDay = day === '*' && month === '*';

  const everyNMinutes = /^\*\/(\d+)$/.exec(minute);
  if (everyNMinutes && hour === '*' && everyDay && weekday === '*') {
    return `a cada ${everyNMinutes[1]} min`;
  }

  if (!/^\d+$/.test(minute) || !everyDay) return expression;

  const everyNHours = /^\*\/(\d+)$/.exec(hour);
  if (everyNHours && weekday === '*') {
    return `a cada ${everyNHours[1]} h, no minuto ${minute}`;
  }

  if (!/^\d+$/.test(hour)) return expression;

  const clock = `${hour.padStart(2, '0')}:${minute.padStart(2, '0')}`;
  if (weekday === '*') return `todo dia às ${clock}`;
  if (/^[0-6]$/.test(weekday)) return `toda ${WEEKDAYS[Number(weekday)]} às ${clock}`;

  return expression;
}

/** "em 4 min (14/08 08:00)" — o quanto falta e a data, porque as duas importam. */
export function formatNextRun(nextRunAt: string | null): string {
  if (!nextRunAt) return 'sem próxima execução';

  const next = new Date(nextRunAt);
  const clock = next.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });

  const seconds = Math.round((next.getTime() - Date.now()) / 1000);

  // O servidor calculou o horário alguns segundos antes de a tela desenhar; um
  // valor no passado significa "está na hora", não que a conta ficou errada.
  if (seconds <= 0) return `a qualquer momento (${clock})`;
  if (seconds < 60) return `em ${seconds} s (${clock})`;
  if (seconds < 3600) return `em ${Math.round(seconds / 60)} min (${clock})`;
  if (seconds < 86_400) return `em ${Math.floor(seconds / 3600)} h (${clock})`;

  return clock;
}

/** `1–25 de 340 execuções`, o rodapé do histórico. */
export function runsPageLabel(page: number, pageSize: number, total: number): string {
  if (total === 0) return 'Nenhuma execução encontrada';

  return `${pageRange(page, pageSize, total)} execuções`;
}
